import React, { useState, useEffect } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import DataTable from "react-data-table-component";
import { get, post } from "../../services/api";
import Select from "react-select";
import { toast } from "react-toastify";
import { customTableStyles } from "../../assets/CustomTableStyles";
import DropdownWithCustomValuesModal from "../../components/DropdownWithCustomValuesModal";

function TimetableSetup() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");

  const [triggerRerender, setTriggerRerender] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [selectedCenterId, setSelectedCenterId] = useState(
    savedCenterId || null
  );
  const [centersData, setCentersData] = useState([]);
  const [timetablesData, setTimetablesData] = useState([]);
  const [classSectionsData, setClassSectionsData] = useState([]);
  const [subjectsData, setSubjectsData] = useState([]);
  const [teachersData, setTeachersData] = useState([]);
  const [periodsData, setPeriodsData] = useState([]);
  const [periodsTypes, setPeriodsTypes] = useState([]);
  const [roomsData, setRoomsData] = useState([]);
  const [daysData, setDaysData] = useState([]);
  const [customDropdownName, setCustomDropdownName] = useState("");
  const [activityCategoryData, setActivityCategoryData] = useState([]);
  const [activityVenueData, setActivityVenueData] = useState([]);
  const [selectedClassSectionId, setSelectedClassSectionId] = useState(null);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [
    isCustomDropdownValuesModalVisible,
    setIsCustomDropdownValuesModalVisible,
  ] = useState(false);
  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      id: 0,
      createdBy: localStorage.getItem("userId"),
      name: "",
      centerId: "",
      classSectionId: "",
      periodId: "",
      periodName: "",
      periodTypeId: "",
      periodType: "",
      dayId: "",
      dayName: "",
      fromTime: "",
      toTime: "",
      subjectId: "",
      subjectName: "",
      roomId: "",
      roomName: "",
      teacherId: "",
      teacherName: "",
      tasks: "",
      lessons: "",
      remarks: "",
    },
  });

  useEffect(() => {
    const fetchCenters = async () => {
      try {
        const response = await get(
          `Dropdown/GetAllCentersByOrganizationId?organizationId=${organizationId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setCentersData(response);

        if (savedCenterId) {
          const selectedCenter = response.find(
            (center) => center.id === parseInt(savedCenterId, 10)
          );

          if (selectedCenter) {
            setValue("centerId", {
              value: selectedCenter.id,
              label: selectedCenter.name,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching centers:", error);
      }
    };

    fetchCenters();
  }, [selectedCenterId]);
  const fetchCommonSettings = async (moduleName, setData) => {
    try {
      const response = await get(
        `CommonSettings/GetCommonSettingDetailByModuleName?moduleName=${moduleName}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setData(response?.commonSettingDetail);
    } catch (error) {
      console.error(`Error fetching ${moduleName}:`, error);
    }
  };
  useEffect(() => {
    const fetchTimetables = async () => {
      try {
        const response = await get(
          `SMSApp/GetTimeTableByCenterClassSectionId?centerId=${selectedCenterId}&classSectionid=${selectedClassSectionId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setTimetablesData(response?.timetable);
      } catch (error) {
        console.error("Error fetching centers:", error);
      }
    };

    if (selectedCenterId && selectedClassSectionId) {
      fetchTimetables();
    }
  }, [selectedCenterId, selectedClassSectionId, triggerRerender]);

  const fetchClassSectionsByCenterId = async (centerId) => {
    try {
      const response = await get(
        `ClassSection/GetAllClassSectionByCenterId?centerId=${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setClassSectionsData(response);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  useEffect(() => {
    if (selectedCenterId) {
      fetchClassSectionsByCenterId(selectedCenterId);
    }
  }, [selectedCenterId]);

  const fetchSubjectsByClassSectionId = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get(
        `MobileApp/GetStudentSectionSubjectDetailByClassSectionId?classSectionsId=${selectedClassSectionId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setSubjectsData(response?.sectionSubjects);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  useEffect(() => {
    if (selectedClassSectionId) {
      fetchSubjectsByClassSectionId();
    }
  }, [selectedClassSectionId, triggerRerender]);

  const fetchPeriods = async () => {
    try {
      const response = await get(
        `CommonSettings/GetCommonSettingDetailByModuleName?moduleName=periodNo`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setPeriodsData(response?.commonSettingDetail);
    } catch (error) {
      console.error("Error fetching centers:", error);
    }
  };

  const fetchPeriodTypes = async () => {
    try {
      const response = await get(
        `CommonSettings/GetCommonSettingDetailByModuleName?moduleName=periodType`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setPeriodsTypes(response?.commonSettingDetail);
    } catch (error) {
      console.error("Error fetching centers:", error);
    }
  };

  const fetchRooms = async () => {
    try {
      const response = await get(
        `CommonSettings/GetCommonSettingDetailByModuleName?moduleName=room`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setRoomsData(response?.commonSettingDetail);
    } catch (error) {
      console.error("Error fetching centers:", error);
    }
  };

  const fetchDays = async () => {
    try {
      const response = await get(
        `CommonSettings/GetCommonSettingDetailByModuleName?moduleName=day`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setDaysData(response?.commonSettingDetail);
    } catch (error) {
      console.error("Error fetching centers:", error);
    }
  };

  useEffect(() => {
    fetchPeriods();
    fetchPeriodTypes();
    fetchRooms();
    fetchDays();
  }, []);

  useEffect(() => {
    const getUsersByCenterIdAndRoleId = async () => {
      try {
        const response = await get(
          `UserAuthentication/GetUsersByCenterIdAndRoleId?centerId=${selectedCenterId}&roleId=${7}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setTeachersData(response);
      } catch (error) {
        console.error("Error fetching teachers:", error);
      }
    };
    getUsersByCenterIdAndRoleId();
  }, [selectedCenterId]);

  const columns = [
    {
      name: "Sr No.",
      cell: (row, index) => (currentPage - 1) * rowsPerPage + index + 1,
      sortable: false,
      width: "60px",
    },
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Day",
      selector: (row) => row.dayName,
      sortable: true,
    },
    // {
    //   name: "Period No.",
    //   selector: (row) => row.periodName,
    //   sortable: true,
    // },
    {
      name: "Subject",
      selector: (row) => row.subjectName,
      sortable: true,
    },
    {
      name: "Teacher",
      selector: (row) => row.teacherName,
      sortable: true,
    },
    {
      name: "Action",
      width: "60px",
      selector: (row) => (
        <div>
          <button
            type="button"
            className="btn p-0"
            onClick={() => handleEdit(row.id)}
          >
            <i className="fas fa-pencil"></i>
          </button>
        </div>
      ),
    },
  ];

  const handleEdit = async (id) => {
    try {
      const response = await get(`SMSApp/GetTimeTableById?id=${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const singleTimetable = response?.timetable;

      const selectedCenter = centersData.find(
        (center) => center.id === singleTimetable.centerId
      );
      const selectedClassSection = classSectionsData.find(
        (classItem) => classItem.id === singleTimetable.classSectionId
      );
      const selectedTeacher = teachersData.find(
        (teacher) => teacher.id === singleTimetable.teacherId
      );
      const selectedPeriodNo = periodsData.find(
        (period) => period.id === singleTimetable.periodId
      );
      const selectedPeriodType = periodsTypes.find(
        (periodType) => periodType.id === singleTimetable.periodTypeId
      );
      const selectedRoom = roomsData.find(
        (room) => room.id === singleTimetable.roomId
      );
      const selectedDay = daysData.find(
        (day) => day.id === singleTimetable.dayId
      );

      const subjectsResponse = await get(
        `Subject/GetSectionSubjectAssignTeacherByClassSectionId?classSectionsId=${singleTimetable.classSectionId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSubjectsData(subjectsResponse?.sectionSubjects);

      const selectedSubject = subjectsResponse?.sectionSubjects.find(
        (subject) => subject.subjectId === singleTimetable.subjectId
      );

      reset({
        ...singleTimetable,
        createdBy: singleTimetable.createdBy,
        updatedBy: localStorage.getItem("userId"),
        name: singleTimetable.name,
        tasks: singleTimetable.tasks,
        lessons: singleTimetable.lessons,
        remarks: singleTimetable.remarks,

        centerId: {
          value: selectedCenter?.id || "",
          label: selectedCenter?.name || "",
        },
        classSectionId: {
          value: selectedClassSection?.id || "",
          label: selectedClassSection?.name || "",
        },
        periodId: {
          value: selectedPeriodNo?.id || "",
          label: selectedPeriodNo?.value || "",
        },
        // periodName: "",
        periodTypeId: {
          value: selectedPeriodType?.id || "",
          label: selectedPeriodType?.value || "",
        },
        // periodType: "",
        dayId: {
          value: selectedDay?.id || "",
          label: selectedDay?.value || "",
        },
        // dayName: "",
        // fromTime: "",
        // toTime: "",
        subjectId: {
          value: selectedSubject?.subjectId || "",
          label: selectedSubject?.subjectName || "",
        },
        // subjectName: "",
        roomId: {
          value: selectedRoom?.id || "",
          label: selectedRoom?.value || "",
        },
        // roomName: "",
        teacherId: {
          value: selectedTeacher?.id || "",
          label:
            selectedTeacher?.firstName + " " + selectedTeacher?.lastName || "",
        },
        // teacherName: "",
      });
    } catch (error) {
      console.error("Error editing holiday:", error);
    }
  };
  const handleCustomDropdownSetting = (dropdownName) => {
    setCustomDropdownName(dropdownName);
    setIsCustomDropdownValuesModalVisible(true);
  };
  // const handleDelete = async (row) => {
  //   const confirmed = window.confirm(
  //     "Are you sure you want to delete this record?"
  //   );

  //   if (!confirmed) return;

  //   try {
  //     const response = await get(
  //       `CenterAttendence/DeleteCalenderHoliday?year=${selectedYear}&centerId=${row.centerId}&id=${row.id}`,
  //       {
  //         headers: { Authorization: `Bearer ${token}` },
  //       }
  //     );

  //     if (response.success) {
  //       toast.success(response.message || "Success!");
  //       fetchCalendars(selectedCenterId, selectedYear);
  //       setTriggerRerender((prev) => !prev);
  //       reset({
  //         id: 0,
  //         name: "",
  //         colorCode: "",
  //         centerId: selectedCenterId,
  //         centerClassId: "",
  //         sectionId: "",
  //         remarks: "",
  //         stdAttnCalenderId: "",
  //         fromDate: "",
  //         toDate: "",
  //         year: selectedYear,
  //         organizationId: organizationId,
  //         createdBy: localStorage.getItem("userId"),
  //       });
  //     } else {
  //       console.error("Error deleting holiday:", response.message);
  //     }
  //   } catch (error) {
  //     console.error("Error on delete:", error);
  //   }
  // };

  // const columns = [
  //   {
  //     name: "Name",
  //     selector: (row) => row.name,
  //     sortable: true,
  //   },
  //   {
  //     name: "Date (From — To)",
  //     selector: (row) =>
  //       `${new Date(row.fromDate).toLocaleDateString("en-GB")} — ${new Date(
  //         row.toDate
  //       ).toLocaleDateString("en-GB")}`,
  //     sortable: true,
  //     width: "200px",
  //   },
  //   {
  //     name: "Type",
  //     selector: (row) => row.calTypeDesc,
  //     sortable: true,
  //   },
  //   {
  //     name: "Days Count",
  //     selector: (row) => row.consecutiveDaysCount,
  //     sortable: true,
  //   },
  //   {
  //     name: "Actions",
  //     selector: (row) => (
  //       <div>
  //         <button
  //           type="button"
  //           className="btn p-0"
  //           onClick={() => handleEdit(row)}
  //         >
  //           <i className="fas fa-pencil"></i>
  //         </button>
  //         <button
  //           type="button"
  //           className="btn p-0 ms-2"
  //           onClick={() => handleDelete(row)}
  //         >
  //           <i className="fas fa-trash"></i>
  //         </button>
  //       </div>
  //     ),
  //   },
  // ];

  const checkAvailability = async (
    classSectionId,
    dayName,
    fromTime,
    toTime,
    columnName,
    value
  ) => {
    const payload = {
      classSectionId,
      dayName,
      fromTime,
      toTime,
      columnName,
      value,
    };

    try {
      const response = await post(`SMSApp/CheckTimeTableAvailablity`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return response?.timetable?.length; // Assuming the API returns the availability result
    } catch (error) {
      console.error("Error checking availability:", error);
      throw error; // Throw error to be handled in the calling function
    }
  };

  const handleDropdownChange = async (
    selectedOption,
    actionMeta,
    columnName
  ) => {
    // If the action was to clear the selection
    if (actionMeta.action === "clear") {
      setValue(columnName, null);
      return;
    }

    // Set the selected value in the form
    setValue(columnName, selectedOption);

    // Call checkAvailability only if a selection is made
    if (selectedOption) {
      const classSectionId = getValues("classSectionId"); // Get current class section ID
      const dayName = getValues("dayId"); // Get selected day
      const fromTime = getValues("fromTime"); // Get start time
      const toTime = getValues("toTime"); // Get end time

      try {
        const alreadyExists = await checkAvailability(
          classSectionId.value,
          dayName.label,
          fromTime,
          toTime,
          columnName,
          selectedOption.value
        );

        if (alreadyExists) {
          alert(`${columnName} is not available for the selected time.`);
          setValue(columnName, null);
        }
      } catch (error) {
        alert("Error checking availability, please try again later.");
      }
    }
  };

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    const formData = {
      ...data,
      centerId: data.centerId.value,
      classSectionId: data.classSectionId.value,
      dayId: data.dayId.value,
      dayName: data.dayId.label,
      periodId: data.periodId.value,
      periodName: data.periodId.label,
      periodTypeId: data.periodTypeId.value,
      periodType: data.periodTypeId.label,
      roomId: data.roomId.value,
      roomName: data.roomId.label,
      subjectId: data.subjectId.value,
      subjectName: data.subjectId.label,
      teacherId: data.teacherId.value,
      teacherName: data.teacherId.label,
    };

    //   ...data,
    //   centerId: data.centerId.value,
    //   centerClassId: data.centerClassId.value,
    //   sectionId: data.sectionId.value,
    // };

    // console.log(formData);
    // return;

    try {
      const response = await post("SMSApp/AddUpdateTimeTable", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        setTriggerRerender((prev) => !prev);

        reset({
          id: 0,
          createdBy: localStorage.getItem("userId"),
          name: "",
          centerId: "",
          classSectionId: "",
          periodId: "",
          periodName: "",
          periodTypeId: "",
          periodType: "",
          dayId: "",
          dayName: "",
          fromTime: "",
          toTime: "",
          subjectId: "",
          subjectName: "",
          roomId: "",
          roomName: "",
          teacherId: "",
          teacherName: "",
          tasks: "",
          lessons: "",
          remarks: "",
        });
      }
    } catch (error) {
      console.error("Error.", error);
    }
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Timetable Setup</h2>
      </div>

      <div className="row g-3">
        <div className="col-md-5">
          <div className="card">
            <div className="card-body">
              <form className="row gy-3 gx-2" onSubmit={handleSubmit(saveData)}>
                <div className="col-md-6">
                  <label className="form-label">
                    Center<span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="centerId"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={centersData.map((center) => ({
                          value: center.id,
                          label: center.name,
                        }))}
                        isDisabled={savedCenterId !== "0"}
                        onChange={(selectedOption, actionMeta) => {
                          if (actionMeta.action === "clear") {
                            setValue("centerId", null);
                            setValue("classSectionId", null);
                            setClassSectionsData([]);
                          } else {
                            setValue("centerId", selectedOption);
                            setValue("classSectionId", null);
                            setValue("teacherId", null);
                            setSelectedCenterId(selectedOption.value);
                            fetchClassSectionsByCenterId(selectedOption.value);
                            setSubjectsData([]);
                          }
                          trigger("centerId");
                        }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        isClearable={!savedCenterId}
                      />
                    )}
                  />
                  {errors.centerId && (
                    <small className="text-danger">
                      {errors.centerId.message}
                    </small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Year Group - Section
                    <span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="classSectionId"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={classSectionsData.map((classSection) => ({
                          value: classSection.id,
                          label: classSection.name,
                        }))}
                        onChange={(selectedOption, actionMeta) => {
                          if (actionMeta.action === "clear") {
                            setValue("classSectionId", null);
                            setValue("subjectId", null);
                            setSubjectsData([]);
                            setTimetablesData([]);
                          } else {
                            setValue("classSectionId", selectedOption);
                            setSelectedClassSectionId(selectedOption.value);
                            setTriggerRerender((prev) => !prev);
                          }
                          trigger("classSectionId");
                        }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        isClearable
                      />
                    )}
                  />
                  {errors.classSectionId && (
                    <small className="text-danger">
                      {errors.classSectionId.message}
                    </small>
                  )}
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    Day<span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="dayId"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={daysData.map((day) => ({
                          value: day.id,
                          label: day.value,
                        }))}
                        onChange={(selectedOption, actionMeta) => {
                          if (actionMeta.action === "clear") {
                            setValue("dayId", null);
                          } else {
                            setValue("dayId", selectedOption);
                          }
                          trigger("dayId");
                        }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        isClearable
                      />
                    )}
                  />
                  {errors.dayId && (
                    <small className="text-danger">
                      {errors.dayId.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Start Time<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register("fromTime", {
                      required: "This field is required.",
                    })}
                    type="time"
                    className="form-control"
                    placeholder=""
                  />
                  {errors.fromTime && (
                    <small className="text-danger">
                      {errors.fromTime.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    End Time<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register("toTime", {
                      required: "This field is required.",
                    })}
                    type="time"
                    className="form-control"
                    placeholder=""
                  />
                  {errors.toTime && (
                    <small className="text-danger">
                      {errors.toTime.message}
                    </small>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label">
                    Teacher<span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="teacherId"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={teachersData.map((teacher) => ({
                          value: teacher.id,
                          label: teacher.firstName + " " + teacher.lastName,
                        }))}
                        onChange={(selectedOption, actionMeta) =>
                          handleDropdownChange(
                            selectedOption,
                            actionMeta,
                            "teacherId"
                          )
                        }
                        // onChange={(selectedOption, actionMeta) => {
                        //   if (actionMeta.action === "clear") {
                        //     setValue("teacherId", null);
                        //   } else {
                        //     setValue("teacherId", selectedOption);
                        //   }
                        //   trigger("teacherId");
                        // }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        isClearable
                      />
                    )}
                  />
                  {errors.teacherId && (
                    <small className="text-danger">
                      {errors.teacherId.message}
                    </small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Subject<span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="subjectId"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={subjectsData.map((subject) => ({
                          value: subject.subjectId,
                          label: subject.subjectName,
                        }))}
                        onChange={(selectedOption, actionMeta) =>
                          handleDropdownChange(
                            selectedOption,
                            actionMeta,
                            "subjectId"
                          )
                        }
                        // onChange={(selectedOption, actionMeta) => {
                        //   if (actionMeta.action === "clear") {
                        //     setValue("subjectId", null);
                        //   } else {
                        //     setValue("subjectId", selectedOption);
                        //   }
                        //   trigger("subjectId");
                        // }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        isClearable
                      />
                    )}
                  />
                  {errors.subjectId && (
                    <small className="text-danger">
                      {errors.subjectId.message}
                    </small>
                  )}
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    Period No<span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="periodId"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={periodsData.map((period) => ({
                          value: period.id,
                          label: period.value,
                        }))}
                        onChange={(selectedOption, actionMeta) =>
                          handleDropdownChange(
                            selectedOption,
                            actionMeta,
                            "periodId"
                          )
                        }
                        // onChange={(selectedOption, actionMeta) => {
                        //   if (actionMeta.action === "clear") {
                        //     setValue("periodId", null);
                        //   } else {
                        //     setValue("periodId", selectedOption);
                        //   }
                        //   trigger("periodId");
                        // }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        isClearable
                      />
                    )}
                  />
                  {errors.periodId && (
                    <small className="text-danger">
                      {errors.periodId.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Period Type<span className="text-danger fs-9">*</span>
                  </label>
                  <div className="d-flex gap-2">
                    <div className="w-100">
                      <Controller
                        name="periodTypeId"
                        control={control}
                        rules={{ required: "This field is required." }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            options={periodsTypes.map((periodType) => ({
                              value: periodType.id,
                              label: periodType.value,
                            }))}
                            onChange={(selectedOption, actionMeta) =>
                              handleDropdownChange(
                                selectedOption,
                                actionMeta,
                                "periodTypeId"
                              )
                            }
                            // onChange={(selectedOption, actionMeta) => {
                            //   if (actionMeta.action === "clear") {
                            //     setValue("periodTypeId", null);
                            //   } else {
                            //     setValue("periodTypeId", selectedOption);
                            //   }
                            //   trigger("periodTypeId");
                            // }}
                            className="react-select-style"
                            classNamePrefix="react-select-style-pref"
                            isClearable
                          />
                        )}
                      />
                      {errors.periodTypeId && (
                        <small className="text-danger">
                          {errors.periodTypeId.message}
                        </small>
                      )}
                    </div>
                    <div>
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        style={{
                          padding: "5.5px 10px",
                        }}
                        onClick={() =>
                          handleCustomDropdownSetting("activityCategoryId")
                        }
                      >
                        <i className="fas fa-plus"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Room<span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="roomId"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={roomsData.map((room) => ({
                          value: room.id,
                          label: room.value,
                        }))}
                        onChange={(selectedOption, actionMeta) =>
                          handleDropdownChange(
                            selectedOption,
                            actionMeta,
                            "roomId"
                          )
                        }
                        // onChange={(selectedOption, actionMeta) => {
                        //   if (actionMeta.action === "clear") {
                        //     setValue("roomId", null);
                        //   } else {
                        //     setValue("roomId", selectedOption);
                        //   }
                        //   trigger("roomId");
                        // }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        isClearable
                      />
                    )}
                  />
                  {errors.roomId && (
                    <small className="text-danger">
                      {errors.roomId.message}
                    </small>
                  )}
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    Name<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register("name", {
                      required: "This field is required.",
                    })}
                    type="text"
                    className="form-control"
                    placeholder=""
                  />
                  {errors.name && (
                    <small className="text-danger">{errors.name.message}</small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">No of Tasks</label>
                  <input
                    {...register("tasks")}
                    type="text"
                    className="form-control"
                    placeholder=""
                  />
                  {errors.tasks && (
                    <small className="text-danger">
                      {errors.tasks.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">No of Lessons</label>
                  <input
                    {...register("lessons")}
                    type="text"
                    className="form-control"
                    placeholder=""
                  />
                  {errors.lessons && (
                    <small className="text-danger">
                      {errors.lessons.message}
                    </small>
                  )}
                </div>

                <div className="col-md-12">
                  <label className="form-label">Description</label>
                  <textarea
                    {...register("remarks")}
                    className="form-control"
                    placeholder=""
                  ></textarea>
                  {errors.remarks && (
                    <small className="text-danger">
                      {errors.remarks.message}
                    </small>
                  )}
                </div>

                <div className="col-12">
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Save" : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-md-7">
          <div className="card">
            <div className="card-body">
              <DataTable
                columns={columns}
                data={timetablesData}
                pagination
                dense
                customStyles={customTableStyles}
                highlightOnHover
                onChangePage={setCurrentPage}
                onChangeRowsPerPage={setRowsPerPage}
              />
            </div>
            {isCustomDropdownValuesModalVisible && (
              <DropdownWithCustomValuesModal
                dropdownName={customDropdownName}
                onClose={() => {
                  setIsCustomDropdownValuesModalVisible(false);
                  if (customDropdownName === "periodTypeId") {
                    fetchCommonSettings("periodTypeId", setPeriodsData);
                  }
                }}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default TimetableSetup;
